import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useSubmitContact, type ContactPayload } from '../../hooks/useSubmitContact';

// Web3Forms' shared hCaptcha sitekey (free plan). Override with your own on paid plans.
const HCAPTCHA_SITEKEY =
  import.meta.env.VITE_HCAPTCHA_SITEKEY || '50b2fe65-b00b-4b9e-ad62-3ba471098be2';

const FIELD =
  'text-fg border-line w-full border-0 border-b bg-transparent py-3 font-sans text-[18px] tracking-[-0.01em] outline-none transition-colors duration-300 focus:border-fg placeholder:text-fg-faint';

const SELECT = `${FIELD} cursor-pointer [&>option]:bg-bg-2 [&>option]:text-fg`;

const KIND_OPTIONS = [
  'New product or prototype',
  'Infrastructure or data systems',
  'Scraping or automation',
  'QA, testing & reliability',
  'Something else'
];

const BUDGET_OPTIONS = ['Not sure yet', 'Under £10k', '£10k – £30k', '£30k – £75k', '£75k+'];

// Returns false during SSR/hydration and true once running on the client, without
// tripping a hydration mismatch — the hydration-safe way to defer client-only UI.
const noopSubscribe = () => () => {};
const useHasHydrated = () =>
  useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false
  );

const Field = ({
  label,
  optional,
  children
}: {
  label: string;
  optional?: boolean;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-3">
    <label className="flex items-baseline justify-between">
      <span>{label}</span>
      {optional && (
        <span className="text-fg-faint text-xs uppercase tracking-[0.08em]">Optional</span>
      )}
    </label>
    {children}
  </div>
);

const Detail = ({
  label,
  children,
  sub
}: {
  label: string;
  children: React.ReactNode;
  sub?: React.ReactNode;
}) => (
  <div className="border-line-faint border-t py-[26px] first:border-t-0 first:pt-0">
    <span className="label mb-3 block">{label}</span>
    <div className="text-[22px] font-medium tracking-[-0.02em]">{children}</div>
    {sub && <div className="text-fg-dim mt-1.5 text-[15px]">{sub}</div>}
  </div>
);

const Contact = () => {
  const { status, submit } = useSubmitContact();
  const [form, setForm] = useState<ContactPayload>({
    name: '',
    email: '',
    kind: KIND_OPTIONS[0],
    budget: BUDGET_OPTIONS[0],
    message: ''
  });
  const [clock, setClock] = useState('--:--:--');
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaError, setCaptchaError] = useState(false);
  const captchaRef = useRef<HCaptcha>(null);
  // hCaptcha is client-only and renders no DOM during the build-time prerender,
  // so gate it until after hydration to keep server and client markup in sync.
  const mounted = useHasHydrated();

  useEffect(() => {
    const tick = () =>
      setClock(
        new Date().toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour12: false })
      );
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const update =
    (key: keyof ContactPayload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    if (status === 'submitting') return;
    if (!captchaToken) {
      setCaptchaError(true);
      return;
    }
    await submit(form, captchaToken);
    // hCaptcha tokens are single-use — reset for any subsequent submission.
    captchaRef.current?.resetCaptcha();
    setCaptchaToken('');
  };

  return (
    <main>
      <section className="pt-30.5 pb-16">
        <div className="wrap">
          <p className="lead mt-0 max-w-[50ch] text-[clamp(20px,1.7vw,24px)]">
            Tell me what you're working on — the rough shape is enough to start. I usually reply
            within a day.
          </p>
        </div>
      </section>

      <section className="wrap">
        <div className="grid items-start gap-14 pb-[88px] md:grid-cols-[1.25fr_0.9fr] md:gap-20 md:pb-[130px]">
          {/* FORM */}
          <form className="flex flex-col gap-7" onSubmit={onSubmit} noValidate>
            <Field label="Your name">
              <input
                className={FIELD}
                type="text"
                placeholder="Jane Founder"
                value={form.name}
                onChange={update('name')}
                required
              />
            </Field>
            <Field label="Email">
              <input
                className={FIELD}
                type="email"
                placeholder="jane@company.com"
                value={form.email}
                onChange={update('email')}
                required
              />
            </Field>
            <Field label="What can I help with?">
              <select className={SELECT} value={form.kind} onChange={update('kind')}>
                {KIND_OPTIONS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </Field>
            <Field label="Indicative budget" optional>
              <select className={SELECT} value={form.budget} onChange={update('budget')}>
                {BUDGET_OPTIONS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </Field>
            <Field label="A few lines about the project">
              <textarea
                className={`${FIELD} min-h-[110px] resize-y leading-[1.5]`}
                placeholder="What you're building, where it's at, and what success looks like."
                value={form.message}
                onChange={update('message')}
              />
            </Field>

            <div className="min-h-[78px]">
              {mounted && (
                <HCaptcha
                  ref={captchaRef}
                  sitekey={HCAPTCHA_SITEKEY}
                  reCaptchaCompat={false}
                  theme="dark"
                  onVerify={(token) => {
                    setCaptchaToken(token);
                    setCaptchaError(false);
                  }}
                  onExpire={() => setCaptchaToken('')}
                  onError={() => setCaptchaToken('')}
                />
              )}
              {captchaError && (
                <p className="mt-2 text-[13px] text-[#facc15]">
                  Please complete the captcha before sending.
                </p>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-5">
              <button className="btn btn--lg" type="submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Sending…' : 'Send message'}{' '}
                <span className="arr">↗</span>
              </button>
              <span className="text-fg-faint max-w-[32ch] text-[13px]">
                Prefer to write directly?{' '}
                <a href="mailto:hello@hvdm.io" className="hover:text-fg transition-colors">
                  hello@hvdm.io
                </a>
              </span>
            </div>

            {status === 'success' && (
              <div className="border-line bg-bg-2 rounded-lg border px-6 py-[22px] text-[15px]">
                Thanks — your message is on its way. I'll get back to you, usually within a day.
              </div>
            )}
            {status === 'error' && (
              <div className="border-line bg-bg-2 rounded-lg border px-6 py-[22px] text-[15px]">
                Something went wrong sending that. Reach me directly at{' '}
                <a href="mailto:hello@hvdm.io" className="underline">
                  hello@hvdm.io
                </a>
                .
              </div>
            )}
          </form>

          {/* DETAILS */}
          <aside className="flex flex-col">
            <Detail label="Email" sub="The fastest way to reach me.">
              <a href="mailto:hello@hvdm.io" className="hover:text-fg-dim transition-colors">
                hello@hvdm.io
              </a>
            </Detail>
            <Detail label="Availability" sub="Taking on a limited number of new engagements.">
              <span className="mr-2.5 inline-block h-2 w-2 animate-[statusPulse_2.4s_ease-in-out_infinite] rounded-full bg-[#facc15] align-middle shadow-[0_0_8px_#facc15]" />
              Open for Q3 2026
            </Detail>
            <Detail label="Based in" sub={<span className="tnum">Local time {clock}</span>}>
              London · UK
            </Detail>
            <Detail label="Elsewhere">
              <div className="flex flex-wrap gap-[18px] text-base">
                <a
                  href="https://github.com/hrvdm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-fg-dim transition-colors">
                  GitHub ↗
                </a>
                <a
                  href="https://www.linkedin.com/in/hrvdm/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-fg-dim transition-colors">
                  LinkedIn ↗
                </a>
              </div>
            </Detail>
          </aside>
        </div>
      </section>
    </main>
  );
};

export default Contact;
